import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { chatAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ChatCircle,
  PaperPlaneTilt,
  Lock,
  User,
  CircleNotch,
  Phone,
  Envelope,
  ArrowLeft,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

// Chat List Page
export const ChatListPage = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await chatAPI.getMyRooms();
      setRooms(response.data);
    } catch (error) {
      console.error('Failed to fetch chat rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="chat-list-page">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900">Conversaciones</h1>
        <p className="text-zinc-600">Tus chats con clientes y proveedores</p>
      </div>

      {rooms.length === 0 ? (
        <Card className="border-zinc-200 rounded-none">
          <CardContent className="p-12 text-center">
            <ChatCircle className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">
              No tienes conversaciones aún
            </h3>
            <p className="text-zinc-500">
              Los chats se habilitan después de que ambas partes confirmen la transacción
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {rooms.map((room) => (
            <Link key={room.room_id} to={`/chat/${room.quotation_id}`}>
              <Card className={`border-zinc-200 rounded-none hover:shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] transition-shadow cursor-pointer ${
                !room.is_active ? 'opacity-60' : ''
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-zinc-900">{room.quotation_name}</h3>
                        {!room.is_active && (
                          <Badge variant="outline" className="rounded-none text-xs">
                            <Lock className="w-3 h-3 mr-1" />
                            Bloqueado
                          </Badge>
                        )}
                        {room.unread_count > 0 && (
                          <Badge className="bg-red-500 rounded-full w-5 h-5 p-0 flex items-center justify-center text-xs">
                            {room.unread_count}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-zinc-500 mt-1">
                        {room.user_role === 'cliente' ? 'Proveedor' : 'Cliente'}: {room.other_user_name}
                      </p>
                      {room.last_message && (
                        <p className="text-sm text-zinc-600 mt-2 truncate">
                          {room.last_message}
                        </p>
                      )}
                    </div>
                    <Badge variant="outline" className="rounded-none capitalize">
                      {room.user_role}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// Chat Room Page
export const ChatRoomPage = () => {
  const { quotationId } = useParams();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [contactInfo, setContactInfo] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchChatData();
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [quotationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChatData = async () => {
    setLoading(true);
    try {
      const roomRes = await chatAPI.getRoom(quotationId);
      setRoom(roomRes.data);

      if (roomRes.data.is_active) {
        const messagesRes = await chatAPI.getMessages(quotationId);
        setMessages(messagesRes.data.messages || []);

        const contactRes = await chatAPI.getContactInfo(quotationId);
        setContactInfo(contactRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch chat data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!room?.is_active) return;
    try {
      const response = await chatAPI.getMessages(quotationId);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      await chatAPI.sendMessage(quotationId, newMessage.trim());
      setNewMessage('');
      await fetchMessages();
    } catch (error) {
      toast.error('Error al enviar el mensaje');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <CircleNotch className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!room) {
    return (
      <div className="text-center py-20">
        <ChatCircle className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-zinc-900">Chat no encontrado</h2>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col" data-testid="chat-room-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <Link to="/chat">
          <Button variant="ghost" className="rounded-none" data-testid="back-to-chats">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-zinc-900">{room.quotation_name}</h1>
          <p className="text-sm text-zinc-600">
            Chat con {room.user_role === 'cliente' ? room.provider_name : room.client_name}
          </p>
        </div>
        {!room.is_active && (
          <Badge variant="outline" className="rounded-none">
            <Lock className="w-4 h-4 mr-1" />
            Chat Bloqueado
          </Badge>
        )}
      </div>

      <div className="flex-1 grid lg:grid-cols-4 gap-6 min-h-0">
        {/* Messages Area */}
        <Card className="lg:col-span-3 border-zinc-200 rounded-none flex flex-col min-h-0">
          {room.is_active ? (
            <>
              <ScrollArea className="flex-1 p-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">
                    No hay mensajes aún. ¡Inicia la conversación!
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.message_id}
                        className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] p-3 ${
                            msg.sender_id === user?.user_id
                              ? 'chat-bubble-client ml-auto'
                              : 'chat-bubble-provider'
                          }`}
                        >
                          <p className="text-xs font-semibold text-zinc-500 mb-1">
                            {msg.sender_name}
                          </p>
                          <p className="text-zinc-900">{msg.content}</p>
                          <p className="text-xs text-zinc-400 mt-1">
                            {new Date(msg.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              <form onSubmit={handleSendMessage} className="p-4 border-t border-zinc-200">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Escribe un mensaje..."
                    className="flex-1 rounded-none"
                    disabled={sending}
                    data-testid="chat-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={sending || !newMessage.trim()}
                    className="btn-primary rounded-none"
                    data-testid="send-message-btn"
                  >
                    {sending ? (
                      <CircleNotch className="w-5 h-5 animate-spin" />
                    ) : (
                      <PaperPlaneTilt className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <CardContent className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Lock className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">Chat Bloqueado</h3>
                <p className="text-zinc-500 max-w-md">
                  El chat se habilitará cuando ambas partes confirmen la transacción.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className={room.client_commission_paid ? 'text-green-600' : 'text-zinc-500'}>
                    Cliente: {room.client_commission_paid ? '✓ Confirmado' : 'Pendiente'}
                  </p>
                  <p className={room.provider_commission_paid ? 'text-green-600' : 'text-zinc-500'}>
                    Proveedor: {room.provider_commission_paid ? '✓ Confirmado' : 'Pendiente'}
                  </p>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Contact Info Sidebar */}
        {room.is_active && contactInfo && (
          <Card className="border-zinc-200 rounded-none">
            <CardHeader className="border-b border-zinc-200">
              <CardTitle className="text-sm">Información de Contacto</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-400 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-zinc-900" />
                </div>
                <div>
                  <p className="font-semibold">{contactInfo.contact?.name}</p>
                  <p className="text-sm text-zinc-500 capitalize">{contactInfo.role}</p>
                </div>
              </div>

              {contactInfo.contact?.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Envelope className="w-4 h-4 text-zinc-400" />
                  <a href={`mailto:${contactInfo.contact.email}`} className="text-amber-600 hover:underline">
                    {contactInfo.contact.email}
                  </a>
                </div>
              )}

              {contactInfo.contact?.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-zinc-400" />
                  <a href={`tel:${contactInfo.contact.phone}`} className="text-amber-600 hover:underline">
                    {contactInfo.contact.phone}
                  </a>
                </div>
              )}

              {contactInfo.contact?.company && (
                <div className="pt-4 border-t border-zinc-200">
                  <p className="text-xs text-zinc-500 uppercase mb-1">Empresa</p>
                  <p className="font-medium">{contactInfo.contact.company}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
